import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { humanize, unescapeHTML, titleize } from 'underscore.string';
import { Link } from 'react-router-dom';
import SessionStore from 'stores/SessionStore';
import moment from 'moment-timezone';
import StateTransition from 'components/v2/state-transition';
import { ProgressCell } from 'components/modules/shipments/renderer';
import Badge from 'components/v2/Badge';
import { sluggify } from 'utils/localization/utils';
import { checkShorthaulAppointmentNeeded, isShipmentCanceled, } from 'helpers/shipments';
import { Info, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { Button, Card, CardContent, DropdownMenu as ShadcnDropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Switch, Tooltip as ShadcnTooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from '@cargomatic/ui';
import ShipmentDetailDateItem from 'components/modules/shipments/details/ShipmentDetailDateItem';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import { isLegManagementEnabled } from 'helpers/legs-helper';
import { isProd } from 'helpers/helper_functions';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { getLocationTimezone } from 'helpers/locations';
import { getFirstStop, getLastStop } from 'helpers/stops';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { withTranslation, Translation, Trans } from 'react-i18next';
import Alert from 'components/ui/Alert';
import BillingStatusRenderer from '../../v2/BillingStatusRenderer';
import { LegNumberSummary } from './LegNumberSummary/LegNumberSummary';
import OpenExceptions from './OpenExceptions';
import DescriptionListItem from '../../v2/DescriptionListItem';
import NextSteps from './next-steps/NextSteps';
import MinimumDocumentRequirements from './MinimumDocumentRequirements';
import { isOrderHazmat } from 'helpers/order-types';
const closedStatuses = ['canceled', 'complete'];
const shipmentTypes = {
    SHORTHAUL: 'shorthaul',
    LONGHAUL: 'longhaul',
    DAYRATE: 'dayrate',
};
const DISABLED_BOL_STATUSES = ['job_canceled', 'job_closed'];
function getBadgeColor(status) {
    if (!closedStatuses.includes(status)) {
        return 'brand';
    }
    return '';
}
const TOOLTIP_KEYS = {
    expectedProgress: 'EXPECTED_PROGRESS_TOOLTIP',
    actualProgress: 'ACTUAL_PROGRESS_TOOLTIP',
    performance: 'PERFORMANCE_TOOLTIP',
    deleteCarrier: 'CLICK_TO_REMOVE_CARRIER',
};
const getTimezone = (zone) => {
    return moment.tz(zone).zoneAbbr();
};
const formatDate = (date, timezone) => {
    const DATE_FORMAT = 'L LT';
    const formattedDate = moment(date).tz(timezone).format(DATE_FORMAT);
    const formattedTimezone = getTimezone(timezone) || '';
    return `${formattedDate} ${formattedTimezone}`;
};
const getDirectionClassName = (direction) => {
    if (direction === 'up') {
        return 'fa fa-arrow-up';
    }
    return 'fa fa-arrow-down';
};
const getAppointmentLabel = (appointment) => {
    const { date, timezone, appointment_unconfirmed } = appointment;
    if (date && !appointment_unconfirmed) {
        return formatDate(date, timezone);
    }
    return React.createElement(Translation, null, (t) => t('NEEDS_APPOINTMENT'));
};
const StopSummary = ({ label, iconDirection, address, date, value, timezone, appointment_unconfirmed, }) => (React.createElement("div", { "data-testid": "div-4c77ba51", className: "stop__address" },
    React.createElement("p", { "data-testid": "p-76340254" },
        React.createElement("strong", { "data-testid": "strong-349caf11" }, label),
        value),
    React.createElement("p", { "data-testid": "p-f56ec6d0" },
        React.createElement("i", { "data-testid": "i-521a0667", className: getDirectionClassName(iconDirection) }),
        ' ',
        address),
    React.createElement("p", { "data-testid": "p-6d91f927" },
        React.createElement("small", { "data-testid": "small-1110e447" },
            React.createElement("i", { "data-testid": "i-0419a98e", className: "fa fa-clock-o" }),
            getAppointmentLabel({
                date,
                timezone,
                appointment_unconfirmed,
            })))));
StopSummary.propTypes = {
    label: PropTypes.string.isRequired,
    iconDirection: PropTypes.oneOf(['up', 'down']),
    address: PropTypes.string.isRequired,
    date: PropTypes.string,
    value: PropTypes.string,
    timezone: PropTypes.string,
    appointment_unconfirmed: PropTypes.bool,
};
function getStopSummaryInfo(stop) {
    const { window_start, window_end, appointment_unconfirmed } = stop;
    const locations = stop.location || [];
    const location = locations[0] || {};
    const timezone = getLocationTimezone(location);
    return {
        location,
        window_start,
        window_end,
        timezone,
        appointment_unconfirmed,
    };
}
function isShipmentWithHazards(shipment) {
    var _a;
    const shipmentHasHazmatDescription = shipment.hasOwnProperty('hazmat_description') &&
        shipment.hazmat_description;
    if (isIntermodalShipmentType(shipment)) {
        const order = (_a = shipment === null || shipment === void 0 ? void 0 : shipment.order) !== null && _a !== void 0 ? _a : {};
        return isOrderHazmat(order);
    }
    return shipmentHasHazmatDescription;
}
class StatusBar extends Component {
    constructor(props) {
        super(props);
        this.handleSaveToggleExposed = (exposed) => {
            this.setState({ exposed, showConfirmToggleExposed: true });
        };
        this.handleConfirmToggleExposed = () => {
            this.setState({ showConfirmToggleExposed: false }, () => {
                this.props.onSaveToggleExposed(this.props.shipment, this.state.exposed);
            });
        };
        this.handleHideToggleExposed = () => this.setState({ showConfirmToggleExposed: false });
        this.handleSelection = (key) => () => {
            switch (key) {
                case 'assign':
                    this.props.onAssignDriver();
                    break;
                case 'email_delivery_order':
                    this.props.onSendDOEMailModal();
                    break;
                case 'accessorials':
                    this.props.onModifyAccessorials();
                    break;
                case 'bol':
                    this.props.onSendBOLMailModal();
                    break;
                case 'rate':
                    this.props.onSendRATEEmailModal();
                    break;
                case 'create_hold':
                    this.props.onCreateHold();
                    break;
                case 'open':
                    this.props.onReOpenShipment();
                    break;
                case 'cancel':
                    this.props.onShowCancelShipmentModal();
                    break;
                case 'clone':
                    this.props.onCloneShipment();
                    break;
                case 'bulk_accessorials':
                    this.props.onBulkAddAccessorial();
                    break;
                case 'generate_carrier_204_tendered':
                    this.props.onGenerateCarrier204('load_tendered');
                    break;
                case 'generate_carrier_204_updated':
                    this.props.onGenerateCarrier204('load_updated');
                    break;
                case 'generate_carrier_204_canceled':
                    this.props.onGenerateCarrier204('load_canceled');
                    break;
                case 'overrideBillingStatus':
                    this.props.onOverrideBillingStatus();
                    break;
                case 'markBillingStatusPendingReview':
                    this.props.onMarkBillingStatusPendingReview();
                    break;
                default:
            }
        };
        this.renderRemoveCarrierButton = () => {
            if (!isIntermodalShipmentType(this.props.shipment))
                return null;
            const { t } = this.props;
            return (React.createElement(TooltipProvider, null,
                React.createElement(ShadcnTooltip, null,
                    React.createElement(TooltipTrigger, { asChild: true },
                        React.createElement(Button, { "data-testid": "button-27e2e87a", variant: "ghost", size: "icon", className: "h-6 w-6 text-muted-foreground hover:text-foreground hover:bg-muted", onClick: this.props.onRemoveCarrier, "aria-label": "remove button" },
                            React.createElement(X, { className: "w-4 h-4" }))),
                    React.createElement(TooltipContent, { side: "right" }, t(TOOLTIP_KEYS.deleteCarrier)))));
        };
        this.transition = (status) => {
            this.props.onShipmentTransition(this.props.shipment, status, {});
        };
        this.state = {
            showConfirmToggleExposed: false,
            exposed: false,
            hazmatAlertDismissed: false,
            preNoticeAlertDismissed: false,
            carrierRejectionAlertDismissed: false,
        };
    }
    canManageAccessorials() {
        const shipment = this.props.shipment;
        const isFinanceUser = SessionStore.getUserRoles().indexOf('finance') > -1;
        if (isIntermodalShipmentType(shipment) && !shipment.carrier) {
            return false;
        }
        if (isFinanceUser) {
            return true;
        }
        if (isIntermodalShipmentType(shipment.shipment_type) && !shipment.current) {
            return false;
        }
        if (!isIntermodalShipmentType(shipment) &&
            (shipment.invoice || shipment.payout)) {
            return false;
        }
        return true;
    }
    canOpenJob() {
        const shipment = this.props.shipment;
        if (shipment.accepted && shipment.current)
            return true;
        // The next two are for shorthaul, since invoice and payout (singular) don't exist on drayage shipments.
        if (!shipment.current && shipment.invoice)
            return false;
        if (!shipment.current && shipment.payout)
            return false;
        if (shipment.current)
            return false;
        return true;
    }
    canDriverAssign() {
        const shipment = this.props.shipment;
        const { status, shipment_type, accepted, current, canceled, payout } = shipment;
        if (checkShorthaulAppointmentNeeded(shipment_type, status))
            return false;
        if (!accepted)
            return true;
        if (accepted && current)
            return true;
        if (!current)
            return false;
        if (!canceled)
            return false;
        return !payout;
    }
    getStatus() {
        const { t } = this.props;
        const status = this.props.shipment.status;
        switch (status) {
            case 'job_closed':
                return t('CLOSED');
            case 'job_canceled':
                return t('CANCELED');
            case 'needs_appt':
                return t('NEEDS_APPT');
            default:
        }
        return t(sluggify(status));
    }
    getQuickPayTag() {
        var _a;
        const { t } = this.props;
        const quick_payment_contract = (_a = this.props.shipment) === null || _a === void 0 ? void 0 : _a.quick_payment_contract;
        if (quick_payment_contract)
            return (React.createElement(Badge, { className: "ml-2", color: "blue" }, t('QUICK_PAY')));
    }
    canClone() {
        const { shipment } = this.props;
        if (isIntermodalShipmentType(shipment) || shipment.order) {
            return false;
        }
        if (shipment && shipment.shipper && shipment.shipper.tags) {
            const tags = shipment.shipper.tags;
            if (tags.indexOf('block_shipment_clones') > -1) {
                return false;
            }
        }
        return true;
    }
    renderCarrierPopoverContent() {
        const { shipment } = this.props;
        return (React.createElement("div", { className: "space-y-1 text-sm" },
            React.createElement("p", { className: "font-semibold" }, unescapeHTML(this.props.shipment.carrier_name)),
            React.createElement("p", { className: "flex items-center gap-1.5" },
                React.createElement(Phone, { className: "w-3 h-3" }),
                shipment.carrier.phone)));
    }
    renderCarrier() {
        var _a;
        if (!this.props.shipment.carrier) {
            return null;
        }
        const { t, shipment: { carrier }, } = this.props;
        const isPortProCarrier = 'portpro' in carrier && ((_a = carrier === null || carrier === void 0 ? void 0 : carrier.portpro) === null || _a === void 0 ? void 0 : _a.enabled);
        return (React.createElement(DescriptionListItem, { dataTestId: "DescriptionListItem-7132f3fd", label: titleize(t('CARRIER', { count: 1 })), className: "" },
            React.createElement("div", { "data-testid": "div-5a585ab5", className: "flex items-center space-x-2" },
                React.createElement("div", { "data-testid": "div-525c15e5", className: "flex items-center space-x-1" },
                    React.createElement("div", { "data-testid": "div-c42e88ca", className: "relative" },
                        React.createElement(Link, { to: `/users/${carrier._id}` },
                            React.createElement("strong", { "data-testid": "strong-66dfa6de" }, unescapeHTML(this.props.shipment.carrier_name))),
                        carrier.mvp ? (React.createElement("img", { "data-testid": "img-d7503e76", src: "/img/cargomatic-circle.png", className: "absolute -top-5 -right-2 w-3", alt: "MVP" })) : null,
                        isPortProCarrier ? (React.createElement("img", { "data-testid": "img-824e88df", src: "/img/icons/portProLogo.png", className: "absolute -top-6 -right-11", title: t('CARRIER_INTEGRATED_WITH_PORTPRO'), alt: t('CARRIER_INTEGRATED_WITH_PORTPRO') })) : null),
                    React.createElement(TooltipProvider, null,
                        React.createElement(ShadcnTooltip, null,
                            React.createElement(TooltipTrigger, { asChild: true },
                                React.createElement("button", { "data-testid": "div-be76024e", className: "inline-flex" },
                                    React.createElement(Info, { className: "w-4 h-4 text-muted-foreground cursor-pointer" }))),
                            React.createElement(TooltipContent, { side: "bottom", className: "max-w-xs" }, this.renderCarrierPopoverContent())))),
                this.renderRemoveCarrierButton())));
    }
    renderShipperPopoverContent() {
        const { shipment: { shipper }, } = this.props;
        return (React.createElement("div", { className: "space-y-1 text-sm" },
            React.createElement("p", { className: "font-semibold" }, unescapeHTML(shipper.company_name)),
            React.createElement("p", null,
                React.createElement(Trans, { i18nKey: "SALES_REP_WITH_NAME", values: { sales_rep: shipper.sales_rep }, components: [React.createElement("strong", { key: "sales-rep-strong", className: "mr-1" })] })),
            React.createElement("p", { className: "flex items-center gap-1.5" },
                React.createElement(Phone, { className: "w-3 h-3" }),
                shipper.phone),
            React.createElement("p", { className: "flex items-center gap-1.5" },
                React.createElement(Mail, { className: "w-3 h-3" }),
                shipper.email)));
    }
    renderShipper() {
        const { t } = this.props;
        return (React.createElement(DescriptionListItem, { dataTestId: "DescriptionListItem-e8764ae4", label: titleize(t('SHIPPER', { count: 1 })), className: "" },
            React.createElement("div", { "data-testid": "div-a66d80f4", className: "flex items-center space-x-2" },
                React.createElement("div", { "data-testid": "div-6b5bd0f2", className: "flex items-center space-x-1" },
                    React.createElement(Link, { to: `/users/${this.props.shipment.shipper._id}` },
                        React.createElement("strong", { "data-testid": "strong-22102e64" }, unescapeHTML(this.props.shipment.shipper_name))),
                    React.createElement(TooltipProvider, null,
                        React.createElement(ShadcnTooltip, null,
                            React.createElement(TooltipTrigger, { asChild: true },
                                React.createElement("button", { "data-testid": "div-3aa4d126", className: "inline-flex" },
                                    React.createElement(Info, { className: "w-4 h-4 text-muted-foreground cursor-pointer" }))),
                            React.createElement(TooltipContent, { side: "bottom", className: "max-w-xs" }, this.renderShipperPopoverContent())))))));
    }
    renderConsigneePopoverContent() {
        const { shipment: { consignee }, } = this.props;
        return (React.createElement("div", { className: "space-y-1 text-sm" },
            React.createElement("p", { className: "font-semibold" }, unescapeHTML(consignee === null || consignee === void 0 ? void 0 : consignee.company_name)),
            React.createElement("p", { className: "flex items-center gap-1.5" },
                React.createElement(Phone, { className: "w-3 h-3" }), consignee === null || consignee === void 0 ? void 0 :
                consignee.phone),
            React.createElement("p", { className: "flex items-center gap-1.5" },
                React.createElement(Mail, { className: "w-3 h-3" }), consignee === null || consignee === void 0 ? void 0 :
                consignee.email)));
    }
    renderConsignee() {
        var _a, _b, _c, _d, _e, _f;
        if (!((_a = this.props.shipment) === null || _a === void 0 ? void 0 : _a.consignee)) {
            return null;
        }
        const { t } = this.props;
        return (React.createElement(DescriptionListItem, { dataTestId: "DescriptionListItem-df4119c2", label: titleize(t('CONSIGNEE', { count: 1 })), className: "" },
            React.createElement("div", { "data-testid": "div-2e11febc", className: "flex items-center space-x-2" },
                React.createElement("div", { "data-testid": "div-548e9d1f", className: "flex items-center space-x-1" },
                    React.createElement(Link, { to: `/users/${(_d = (_c = (_b = this.props) === null || _b === void 0 ? void 0 : _b.shipment) === null || _c === void 0 ? void 0 : _c.consignee) === null || _d === void 0 ? void 0 : _d._id}` },
                        React.createElement("strong", { "data-testid": "strong-2c0785c7" }, unescapeHTML((_f = (_e = this.props) === null || _e === void 0 ? void 0 : _e.shipment) === null || _f === void 0 ? void 0 : _f.consignee_name))),
                    React.createElement(TooltipProvider, null,
                        React.createElement(ShadcnTooltip, null,
                            React.createElement(TooltipTrigger, { asChild: true },
                                React.createElement("button", { "data-testid": "div-4f41697d", className: "inline-flex" },
                                    React.createElement(Info, { className: "w-4 h-4 text-muted-foreground cursor-pointer" }))),
                            React.createElement(TooltipContent, { side: "bottom", className: "max-w-xs" }, this.renderConsigneePopoverContent())))))));
    }
    renderDriverPopoverContent() {
        const { shipment } = this.props;
        return (React.createElement("div", { className: "space-y-1 text-sm" },
            React.createElement("p", { className: "font-semibold" }, unescapeHTML(shipment.driver_name)),
            React.createElement("p", { className: "flex items-center gap-1.5" },
                React.createElement(Phone, { className: "w-3 h-3" }),
                shipment.driver.phone)));
    }
    renderDriver() {
        if (!this.props.shipment.driver)
            return null;
        const { t } = this.props;
        return (React.createElement(DescriptionListItem, { dataTestId: "DescriptionListItem-8043a6a8", className: "", label: titleize(t('DRIVER', { count: 1 })) },
            React.createElement("div", { "data-testid": "div-cf245287", className: "flex items-center space-x-2" },
                React.createElement("div", { "data-testid": "div-863fdc99", className: "flex items-center space-x-1" },
                    React.createElement(Link, { to: `/users/${this.props.shipment.driver._id}` },
                        React.createElement("strong", { "data-testid": "strong-42e5d6f6" }, unescapeHTML(this.props.shipment.driver_name))),
                    React.createElement(TooltipProvider, null,
                        React.createElement(ShadcnTooltip, null,
                            React.createElement(TooltipTrigger, { asChild: true },
                                React.createElement("button", { "data-testid": "div-e5a952d5", className: "inline-flex" },
                                    React.createElement(Info, { className: "w-4 h-4 text-muted-foreground cursor-pointer" }))),
                            React.createElement(TooltipContent, { side: "bottom", className: "max-w-xs" }, this.renderDriverPopoverContent())))),
                this.renderRemoveCarrierButton())));
    }
    renderReviewDate() {
        const { t, shipment } = this.props;
        return (React.createElement(ShipmentDetailDateItem, { dataTestId: "shipmentStatusBarReviewDate", className: "mt-2 lg:col-span-2 z-[45]", titleClassName: "!mb-0 text-sm font-medium text-gray-500", title: t('REVIEW_DATE'), key: "Review Date", heapClassName: "shipment-shipmentDetails-shipmentStatusBarReviewDate heap", value: shipment.review_date, editable: true, onSave: (event, value) => {
                ShipmentsActionCreators.updateImportantDates(shipment, event, value);
            }, param: "review_date", useNamedUpdate: true }));
    }
    renderFirstStop() {
        const { t } = this.props;
        const firstStop = getFirstStop(this.props.shipment.stops);
        if (!firstStop) {
            return null;
        }
        const stopInfo = getStopSummaryInfo(firstStop);
        return (React.createElement(StopSummary, { label: `${t('STARTING_ORIGIN')} `, value: humanize(unescapeHTML(stopInfo.location.name)), address: stopInfo.location.address, iconDirection: "up", date: stopInfo.window_end, timezone: stopInfo.timezone, appointment_unconfirmed: stopInfo.appointment_unconfirmed }));
    }
    renderLastStop() {
        const { t } = this.props;
        const lastStop = getLastStop(this.props.shipment.stops);
        if (!lastStop) {
            return null;
        }
        const stopInfo = getStopSummaryInfo(lastStop);
        return (React.createElement(StopSummary, { label: `${t('FINISING_DESTINATION')} `, value: humanize(unescapeHTML(stopInfo.location.name)), address: stopInfo.location.address, iconDirection: "down", date: stopInfo.window_end, timezone: stopInfo.timezone, appointment_unconfirmed: stopInfo.appointment_unconfirmed }));
    }
    renderStateTransitions() {
        return (React.createElement("div", { "data-testid": "div-aa55e577", className: "" },
            React.createElement(StateTransition, { dataTestId: "StateTransition-9cefc01b", progress: this.props.progress ? this.props.progress : null, regress: this.props.regress ? this.props.regress : null, onClick: this.transition })));
    }
    renderProgress() {
        const { shipment: { expected_progress_percentage, actual_progress_percentage, total_expected_progress_percentage, performance_percentage, }, t, } = this.props;
        return (React.createElement("div", { "data-testid": "div-dfd7845f" },
            React.createElement("strong", { "data-testid": "strong-f9c8c16e" },
                t('EXPECTED_PROGRESS'),
                " "),
            React.createElement(TooltipProvider, null,
                React.createElement(ShadcnTooltip, null,
                    React.createElement(TooltipTrigger, { asChild: true },
                        React.createElement("span", { className: "inline-flex" },
                            React.createElement(Info, { className: "w-4 h-4 text-muted-foreground" }))),
                    React.createElement(TooltipContent, null, t(TOOLTIP_KEYS.expectedProgress)))),
            React.createElement(ProgressCell, { actual: expected_progress_percentage, expected: total_expected_progress_percentage }),
            React.createElement("strong", { "data-testid": "strong-67624d13" },
                t('ACTUAL_PROGRESS'),
                " "),
            React.createElement(TooltipProvider, null,
                React.createElement(ShadcnTooltip, null,
                    React.createElement(TooltipTrigger, { asChild: true },
                        React.createElement("span", { className: "inline-flex" },
                            React.createElement(Info, { className: "w-4 h-4 text-muted-foreground" }))),
                    React.createElement(TooltipContent, null, t(TOOLTIP_KEYS.actualProgress)))),
            React.createElement(ProgressCell, { actual: actual_progress_percentage, expected: total_expected_progress_percentage }),
            React.createElement("strong", { "data-testid": "strong-450a928d" },
                t('PERFORMANCE'),
                " "),
            React.createElement(TooltipProvider, null,
                React.createElement(ShadcnTooltip, null,
                    React.createElement(TooltipTrigger, { asChild: true },
                        React.createElement("span", { className: "inline-flex" },
                            React.createElement(Info, { className: "w-4 h-4 text-muted-foreground" }))),
                    React.createElement(TooltipContent, null, t(TOOLTIP_KEYS.performance)))),
            React.createElement(ProgressCell, { actual: performance_percentage, expected: 100 })));
    }
    renderActionsMenu() {
        var _a, _b;
        let { shipment, t } = this.props;
        const { status, shipment_type } = shipment;
        const isLegacyAccounting = !isLegManagementEnabled((_b = (_a = this.props) === null || _a === void 0 ? void 0 : _a.shipment) === null || _b === void 0 ? void 0 : _b.order);
        const shouldDisableBOL = DISABLED_BOL_STATUSES.includes(status) || isShipmentWithHazards(shipment);
        const shouldShow = shipment_type === shipmentTypes.SHORTHAUL ||
            shipment_type === shipmentTypes.LONGHAUL ||
            shipment_type === shipmentTypes.DAYRATE;
        const shouldShowRATE = !shouldShow;
        const shouldDisableAccessorial = isIntermodalShipmentType(shipment) && status === 'job_canceled'
            ? isShipmentCanceled(shipment)
            : !this.canManageAccessorials();
        return (React.createElement(ShadcnDropdownMenu, { "data-testid": "DropdownMenu-8e367603" },
            React.createElement(DropdownMenuTrigger, { asChild: true },
                React.createElement(Button, { variant: "outline", "data-testid": "DropdownMenu-8e367603" },
                    t('ACTIONS'),
                    React.createElement(ChevronDown, { className: "ml-2 h-4 w-4" }))),
            React.createElement(DropdownMenuContent, { align: "end" },
                React.createElement(DropdownMenuItem, { disabled: !this.canDriverAssign(), onSelect: this.handleSelection('assign') }, t('ASSIGN')),
                isIntermodalShipmentType(this.props.shipment) && (React.createElement(DropdownMenuItem, { onSelect: this.handleSelection('email_delivery_order') }, t('EMAIL_DELIVERY_ORDER'))),
                React.createElement(DropdownMenuItem, { disabled: !this.canOpenJob(), onSelect: this.handleSelection('open') }, t('REOPEN')),
                React.createElement(DropdownMenuItem, { disabled: shouldDisableBOL, onSelect: this.handleSelection('bol') }, t('GENERATE_BOL')),
                React.createElement(DropdownMenuItem, { asChild: true },
                    React.createElement("a", { href: `/tracking?id=${this.props.shipment.shipment_id}`, target: "_blank", rel: "noreferrer" }, t('TRACKING_LINK'))),
                React.createElement(DropdownMenuItem, { onSelect: this.handleSelection('create_hold') }, t('CREATE_HOLD')),
                !shouldShowRATE && (React.createElement(DropdownMenuItem, { onSelect: this.handleSelection('rate') }, t('GENERATE_RATE_CONFIRMATION'))),
                !isProd() && (React.createElement(DropdownMenuItem, { onSelect: this.handleSelection('generate_carrier_204_tendered') }, t('SEND_EDI204_TENDERED'))),
                !isProd() && (React.createElement(DropdownMenuItem, { onSelect: this.handleSelection('generate_carrier_204_updated') }, t('SEND_EDI204_UPDATED'))),
                !isProd() && (React.createElement(DropdownMenuItem, { onSelect: this.handleSelection('generate_carrier_204_canceled') }, t('SEND_EDI204_CANCELED'))),
                React.createElement(DropdownMenuItem, { disabled: shouldDisableAccessorial, onSelect: this.handleSelection('accessorials') }, t('ACCESSORIAL', { count: 3 })),
                isIntermodalShipmentType(this.props.shipment) && (React.createElement(DropdownMenuItem, { disabled: shouldDisableAccessorial, onSelect: this.handleSelection('bulk_accessorials') }, t('BULK_ACCESSORIALS'))),
                React.createElement(DropdownMenuItem, { disabled: !this.canClone(), onSelect: this.handleSelection('clone') }, t('CLONE')),
                React.createElement(DropdownMenuItem, { disabled: !this.props.shipment.current, onSelect: this.handleSelection('cancel') }, t('CANCEL')),
                SessionStore.isBillingStatusAdmin() && isLegacyAccounting && (React.createElement(DropdownMenuItem, { onSelect: this.handleSelection('overrideBillingStatus') }, t('OVERRIDE_BILLING_STATUS'))))));
    }
    renderHazmatAlert() {
        var _a;
        const { t, shipment } = this.props;
        const order = (_a = shipment === null || shipment === void 0 ? void 0 : shipment.order) !== null && _a !== void 0 ? _a : {};
        const hasHazmat = isOrderHazmat(order);
        if (!hasHazmat || this.state.hazmatAlertDismissed) {
            return null;
        }
        return (React.createElement(Alert, { dataTestId: "Alert-05b61aey", type: "warning", className: "mb-4", title: t('HAZMAT_WARNING_HEADER'), canClose: true, onClose: () => this.setState({ hazmatAlertDismissed: true }) },
            React.createElement("p", { "data-testid": "p-7ddcba2f" }, t('HAZMAT_WARNING_BODY'))));
    }
    renderPreNoticeAlert() {
        const { t, shipment } = this.props;
        const { pre_arrival = false } = shipment || {};
        if (!pre_arrival ||
            !featureEnabled(CONFIGURED_FEATURES.AMAZON_PRE_ARRIVAL) ||
            this.state.preNoticeAlertDismissed) {
            return null;
        }
        return (React.createElement(Alert, { dataTestId: "Alert-07bf1a4d", type: "error", className: "mb-4", title: t('PRE_NOTICE_SHIPMENT_HEADER'), canClose: true, onClose: () => this.setState({ preNoticeAlertDismissed: true }) },
            React.createElement("p", { "data-testid": "p-7ddcba2f" }, t('PRE_NOTICE_SHIPMENT_BODY'))));
    }
    renderCarrierRejectionAlert() {
        var _a, _b, _c;
        const { t, shipment } = this.props;
        const hasCarrierRejectedShipment = (_c = (_b = (_a = shipment.carrier) === null || _a === void 0 ? void 0 : _a.portpro) === null || _b === void 0 ? void 0 : _b.rejected) === null || _c === void 0 ? void 0 : _c.includes(shipment._id);
        if (!hasCarrierRejectedShipment ||
            this.state.carrierRejectionAlertDismissed) {
            return null;
        }
        return (React.createElement(Alert, { dataTestId: "Alert-8852c662", type: "warning", className: "mb-4", title: t('REJECTION_WARNING_HEADER'), canClose: true, onClose: () => this.setState({ carrierRejectionAlertDismissed: true }) },
            React.createElement("p", { "data-testid": "p-ea0c6bf1" }, t('REJECTION_WARNING_BODY'))));
    }
    render() {
        var _a, _b, _c, _d, _e, _f, _g;
        const { shipment: { status, stops, shipment_id, shipment_type, payables, on_hold, carrier, }, onNextActionsDateClick, t, } = this.props;
        const isDrayageOrRailShipment = shipment_type &&
            (shipment_type === 'drayage' || shipment_type === 'rail');
        let exposeHidden = true;
        const carrierCost = payables
            .filter((c) => c.message && c.message.includes('Carrier Cost'))
            .reduce((sum, c) => sum + (c.value || 0), 0);
        const hasAutoExposeDrayageShipmentTag = (((_b = (_a = this.props.shipment) === null || _a === void 0 ? void 0 : _a.shipper) === null || _b === void 0 ? void 0 : _b.tags) || []).includes('auto_expose_drayage_shipments');
        if (isDrayageOrRailShipment && !carrier) {
            exposeHidden = false;
        }
        if (!stops) {
            return null;
        }
        return (React.createElement(Card, { "data-testid": "div-f81b29f7", className: "mb-4" },
            React.createElement(CardContent, { className: "relative pt-6 lg:grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6" },
                React.createElement("div", { "data-testid": "div-a00cfcb4", className: "lg:col-span-12" },
                    this.renderPreNoticeAlert(),
                    this.renderHazmatAlert(),
                    this.renderCarrierRejectionAlert(),
                    React.createElement(OpenExceptions, { shipment: this.props.shipment }),
                    React.createElement("div", { "data-testid": "div-2f90417a", className: "flex items-center justify-between flex-wrap" },
                        React.createElement("div", { "data-testid": "div-0d8b52c9", className: "flex items-center flex-wrap gap-8" },
                            React.createElement("div", { className: "flex items-center gap-2" },
                                React.createElement("h3", { "data-testid": "h3-4e21e032", className: "my-0" }, shipment_id),
                                React.createElement(Badge, { dataTestId: "Badge-41e328b1", className: "", color: getBadgeColor(status) }, this.getStatus()),
                                !isLegManagementEnabled((_d = (_c = this.props) === null || _c === void 0 ? void 0 : _c.shipment) === null || _d === void 0 ? void 0 : _d.order) && (React.createElement(BillingStatusRenderer, Object.assign({}, this.props))),
                                on_hold && (React.createElement(Badge, { dataTestId: "Badge-a8f30095", className: "", color: "red" }, t('ON_HOLD'))),
                                this.getQuickPayTag()),
                            isIntermodalShipmentType(this.props.shipment) &&
                                isLegManagementEnabled((_e = this.props.shipment) === null || _e === void 0 ? void 0 : _e.order) && (React.createElement(LegNumberSummary, { id: (_g = (_f = this.props) === null || _f === void 0 ? void 0 : _f.shipment) === null || _g === void 0 ? void 0 : _g._id }))),
                        React.createElement("div", { "data-testid": "div-c4cdb935", className: "flex flex-wrap items-center my-4 lg:my-0 gap-4" },
                            !exposeHidden && (React.createElement("div", { "data-testid": "div-881f5657", className: "flex items-center" },
                                React.createElement("label", { "data-testid": "label-8b29d2bf", className: "mb-0 mr-2 form-label" }, t('EXPOSE')),
                                React.createElement(Switch, { checked: this.props.shipment.exposed, onCheckedChange: this.handleSaveToggleExposed }))),
                            this.renderStateTransitions(),
                            React.createElement("div", { "data-testid": "div-82424d60", className: "" }, this.renderActionsMenu()),
                            this.props.showCloseButton && this.props.onClose && (React.createElement("div", { "data-testid": "div-ccbd4672", className: "absolute top-0 right-0" },
                                React.createElement(Button, { size: "icon", variant: "icon", "data-testid": "button-7904e57e", className: "w-10 h-10", onClick: () => this.props.onClose(), "aria-label": "close button" },
                                    React.createElement(X, { className: "w-4 h-4" }))))))),
                React.createElement("div", { "data-testid": "div-56f513c8", className: "lg:col-span-4" },
                    React.createElement("div", { "data-testid": "div-8cadbdeb", className: "lg:grid lg:grid-cols-2 lg:gap-2" },
                        this.renderShipper(),
                        this.renderCarrier(),
                        this.renderDriver(),
                        this.renderConsignee(),
                        this.renderReviewDate())),
                featureEnabled(CONFIGURED_FEATURES.SSR) &&
                    isIntermodalShipmentType(this.props.shipment) ? (React.createElement(NextSteps, { onNextActionsDateClick: (item) => onNextActionsDateClick(item), shipment: this.props.shipment })) : (React.createElement(React.Fragment, null,
                    React.createElement("div", { "data-testid": "div-d6592829", className: "lg:col-span-3" }, this.renderFirstStop()),
                    React.createElement("div", { "data-testid": "div-8548b554", className: "lg:col-span-3" }, this.renderLastStop()),
                    React.createElement("div", { "data-testid": "div-9ff9482f", className: "lg:col-span-2" }, this.renderProgress()))),
                featureEnabled(CONFIGURED_FEATURES.SSR) &&
                    isIntermodalShipmentType(this.props.shipment) && (React.createElement(MinimumDocumentRequirements, { shipment: this.props.shipment })),
                this.state.showConfirmToggleExposed && (React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-b5242d33", title: this.state.exposed
                        ? t('EXPOSE_SHIPMENT')
                        : t('UNEXPOSE_SHIPMENT'), message: !hasAutoExposeDrayageShipmentTag && carrierCost <= 0
                        ? t('WARNING_CARRIER_COST_ARE_YOU_SURE')
                        : t('ARE_YOU_SURE'), show: this.state.showConfirmToggleExposed, onConfirm: this.handleConfirmToggleExposed, onHide: this.handleHideToggleExposed })))));
    }
}
StatusBar.propTypes = {
    shipment: PropTypes.shape({
        shipment_id: PropTypes.string.isRequired,
        shipper_name: PropTypes.string,
        carrier_name: PropTypes.string,
        driver_name: PropTypes.string,
        carrier: PropTypes.shape({
            _id: PropTypes.string,
            email: PropTypes.string,
            phone: PropTypes.string,
        }),
        driver: PropTypes.shape({
            _id: PropTypes.string,
            email: PropTypes.string,
            phone: PropTypes.string,
        }),
        shipper: PropTypes.shape({
            _id: PropTypes.string,
            email: PropTypes.string,
            phone: PropTypes.string,
            sales_rep: PropTypes.string,
        }),
        stops: PropTypes.arrayOf(PropTypes.shape({
            location: PropTypes.arrayOf(PropTypes.shape({
                name: PropTypes.string.isRequired,
                address: PropTypes.string.isRequired,
            })),
        })).isRequired,
        on_hold: PropTypes.bool.isRequired,
        pre_arrival: PropTypes.bool,
    }),
    onRemoveCarrier: PropTypes.func,
};
export default withTranslation()(StatusBar);
